/**
 * 無効アドレス整理ダイアログ（B-20）。
 * mail_send_logs の失敗ログ（501 等）から無効アドレスを抽出し、該当 contact を削除/除去する。
 */
import { useEffect, useState } from 'react';

import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';
import {
  useCleanupInvalidEmailContacts,
  useFindInvalidEmailContacts,
  type InvalidContactRow,
} from '@/hooks/useMailContacts';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InvalidContactsDialog({ open, onOpenChange }: Props) {
  const findMutation = useFindInvalidEmailContacts();
  const cleanupMutation = useCleanupInvalidEmailContacts();
  const [rows, setRows] = useState<InvalidContactRow[] | null>(null);

  // 開いたらレポート取得
  useEffect(() => {
    if (!open) {
      setRows(null);
      return;
    }
    findMutation
      .mutateAsync()
      .then((res) => setRows(res.rows))
      .catch((err) =>
        toast.error(`抽出に失敗: ${err instanceof Error ? err.message : String(err)}`),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const matched = (rows ?? []).filter((r) => r.contactId);
  const deleteCount = matched.filter((r) => r.otherEmailCount === 0).length;
  const trimCount = matched.filter((r) => r.otherEmailCount > 0).length;

  const handleCleanup = async () => {
    if (matched.length === 0) return;
    if (
      !window.confirm(
        `無効アドレス ${matched.length} 件を整理します。\n` +
          `・contact 削除: ${deleteCount} 件（そのメールが唯一）\n` +
          `・メールのみ除去: ${trimCount} 件（他に有効メールあり）\n` +
          `元に戻せません。よろしいですか？`,
      )
    )
      return;
    try {
      const res = await cleanupMutation.mutateAsync(undefined);
      toast.success(
        `整理しました（contact削除 ${res.deletedContacts} / メール除去 ${res.updatedContacts} / 無効計 ${res.removedEmails}）`,
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(`削除に失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>無効アドレスの整理</DialogTitle>
          <DialogDescription>
            一括/予約配信で SES に拒否された宛先（501 Invalid RCPT 等）を送信ログから抽出し、
            配信先リスト（オーディエンス）から削除します。
          </DialogDescription>
        </DialogHeader>

        {findMutation.isPending || rows === null ? (
          <div className="flex items-center justify-center p-10 text-[var(--text-muted)]">
            <Loader2 className="size-5 animate-spin mr-2" />
            送信ログを解析中…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-[var(--text-secondary)]">
            無効アドレスは見つかりませんでした。
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 text-xs rounded bg-[var(--accent-amber-subtle)] border border-[var(--accent-amber)]/40 px-3 py-2 text-[var(--accent-amber)]">
              <AlertTriangle className="size-4 flex-shrink-0" />
              <span>
                無効アドレス <strong>{rows.length}</strong> 件（うち配信先一致{' '}
                <strong>{matched.length}</strong> 件）。
                contact 削除 {deleteCount} 件 / メールのみ除去 {trimCount} 件。
              </span>
            </div>
            <div className="mt-3 max-h-72 overflow-y-auto rounded border border-[var(--border)] divide-y divide-[var(--border)]">
              {rows.map((r) => (
                <div
                  key={r.email}
                  className="flex items-center justify-between gap-3 px-3 py-2 text-xs"
                >
                  <div className="min-w-0">
                    <div className="font-mono truncate">{r.email}</div>
                    <div className="text-[10px] text-[var(--text-muted)] truncate">
                      {r.contactName ? `${r.contactName}` : '（配信先に無し）'}
                      {' — '}
                      {r.reason}
                    </div>
                  </div>
                  {r.contactId ? (
                    <span
                      className={
                        r.otherEmailCount === 0
                          ? 'text-[var(--accent-red)] flex-shrink-0'
                          : 'text-[var(--text-muted)] flex-shrink-0'
                      }
                    >
                      {r.otherEmailCount === 0
                        ? 'contact削除'
                        : `メール除去（他${r.otherEmailCount}）`}
                    </span>
                  ) : (
                    <span className="text-[var(--text-muted)] flex-shrink-0">対象なし</span>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            閉じる
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleCleanup}
            disabled={
              cleanupMutation.isPending ||
              findMutation.isPending ||
              matched.length === 0
            }
          >
            {cleanupMutation.isPending ? (
              <Loader2 className="size-3.5 mr-1 animate-spin" />
            ) : (
              <Trash2 className="size-3.5 mr-1" />
            )}
            {matched.length} 件を削除
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
